/**
 * Thin wrapper around Nominatim forward/reverse geocoding and haversine
 * nearest-location lookup. Used by map-picker and geocoding modes to resolve
 * addresses or map clicks to the nearest port/area from the locations dataset.
 *
 * Nominatim usage: https://nominatim.org/release-docs/develop/api/Overview/
 * Requires a valid User-Agent (same as server-side Geocoder config).
 */

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';
const USER_AGENT = 'NowlunApp/1.0 (contact@nowlun.com)';
const DEFAULT_TIMEOUT_MS = 10000;

/**
 * Haversine distance between two points in km (spherical earth).
 *
 * @param {number} lat1 - Latitude of first point (degrees)
 * @param {number} lng1 - Longitude of first point (degrees)
 * @param {number} lat2 - Latitude of second point (degrees)
 * @param {number} lng2 - Longitude of second point (degrees)
 * @returns {number} Distance in kilometres
 */
export function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth radius in km
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Find the location from the dataset that is nearest to the given coordinates.
 * Locations without valid lat/lng are skipped.
 *
 * @param {number} lat - Latitude (degrees)
 * @param {number} lng - Longitude (degrees)
 * @param {Array<{ lat?: number, lng?: number, [key: string]: unknown }>} locations - Dataset from selectors (or any list with optional lat/lng)
 * @returns {{ location: object, distanceKm: number } | null} Nearest location and distance, or null if none have coordinates
 */
export function findNearestLocation(lat, lng, locations) {
  if (!locations || !Array.isArray(locations) || locations.length === 0) {
    return null;
  }

  const withCoords = locations.filter(
    (loc) =>
      typeof loc.lat === 'number' &&
      typeof loc.lng === 'number' &&
      Number.isFinite(loc.lat) &&
      Number.isFinite(loc.lng)
  );

  if (withCoords.length === 0) return null;

  let nearest = withCoords[0];
  let minDist = haversineDistance(lat, lng, nearest.lat, nearest.lng);

  for (let i = 1; i < withCoords.length; i++) {
    const loc = withCoords[i];
    const d = haversineDistance(lat, lng, loc.lat, loc.lng);
    if (d < minDist) {
      minDist = d;
      nearest = loc;
    }
  }

  return { location: nearest, distanceKm: minDist };
}

/**
 * Fetch JSON from Nominatim with required User-Agent and optional timeout.
 *
 * @param {string} url - Full URL (with query params)
 * @param {number} [timeoutMs] - Request timeout in ms
 * @returns {Promise<object|object[]>} Parsed JSON
 */
function nominatimFetch(url, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, {
    method: 'GET',
    headers: { 'Accept': 'application/json', 'User-Agent': USER_AGENT },
    signal: controller.signal,
  })
    .then((res) => {
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error(`Nominatim error: ${res.status}`);
      return res.json();
    })
    .catch((err) => {
      clearTimeout(timeoutId);
      throw err;
    });
}

// Strip "Port of <X>" → "<X>" and "<X> Port" → "<X>" so Nominatim finds the
// city rather than failing on the word "port". Safe for proper names like
// "Port Said" which don't match either pattern.
function normalizePortQuery(query) {
  return query
    .replace(/^port\s+of\s+/i, '')
    .replace(/\s+port$/i, '')
    .trim();
}

// Returns an ordered list of query strings to try. Starts with the original,
// then progressively shorter comma-suffix slices, then normalized variants.
// Fallback candidates must keep ≥ 2 comma parts to avoid resolving a bare
// country name to its geographic centroid instead of the intended city.
//
// "23 Rue X, Alexandria, Egypt" → ["23 Rue X, Alexandria, Egypt", "Alexandria, Egypt"]
// "Alexandria, Egypt"           → ["Alexandria, Egypt"]  (no unsafe fallback)
// "Port of Rotterdam, Netherlands" → [..., "Rotterdam, Netherlands"]
function buildQueryCandidates(query) {
  const candidates = [query];

  const parts = query.split(',').map((p) => p.trim()).filter(Boolean);
  for (let i = 1; i < parts.length; i++) {
    if (parts.length - i >= 2) {
      candidates.push(parts.slice(i).join(', '));
    }
  }

  const normalized = normalizePortQuery(query);
  if (normalized !== query) {
    candidates.push(normalized);
    const normParts = normalized.split(',').map((p) => p.trim()).filter(Boolean);
    for (let i = 1; i < normParts.length; i++) {
      if (normParts.length - i >= 2) {
        candidates.push(normParts.slice(i).join(', '));
      }
    }
  }

  return [...new Set(candidates)];
}

function fetchGeocodeOnce(query, timeoutMs) {
  const params = new URLSearchParams({ q: query, format: 'json', limit: '1' });
  return nominatimFetch(`${NOMINATIM_BASE}/search?${params.toString()}`, timeoutMs)
    .then((results) => {
      if (!Array.isArray(results) || results.length === 0) return null;
      const r = results[0];
      const lat = parseFloat(r.lat);
      const lng = parseFloat(r.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng, display_name: r.display_name || `${lat}, ${lng}` };
    })
    .catch(() => null);
}

async function tryQueryCandidates(candidates, timeoutMs) {
  for (const q of candidates) {
    const result = await fetchGeocodeOnce(q, timeoutMs);
    if (result) return result;
  }
  return null;
}

/**
 * Forward geocode: address or place name → coordinates and display name.
 * Tries progressively simplified versions of the query before giving up,
 * so partial or verbose addresses still resolve without falling back to
 * dangerously generic single-term queries.
 *
 * @param {string} query - Address or place name to search
 * @param {number} [timeoutMs] - Request timeout in ms
 * @returns {Promise<{ lat: number, lng: number, display_name: string } | null>} First result or null if none
 */
export function forwardGeocode(query, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const trimmed = (query || '').trim();
  if (!trimmed) return Promise.resolve(null);
  return tryQueryCandidates(buildQueryCandidates(trimmed), timeoutMs);
}

/**
 * Reverse geocode: coordinates → address / display name.
 *
 * @param {number} lat - Latitude (degrees)
 * @param {number} lng - Longitude (degrees)
 * @param {number} [timeoutMs] - Request timeout in ms
 * @returns {Promise<{ lat: number, lng: number, display_name: string } | null>} Result or null on failure
 */
export function reverseGeocode(lat, lng, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
    return Promise.resolve(null);
  }

  const params = new URLSearchParams({
    lat: String(latNum),
    lon: String(lngNum),
    format: 'json',
  });
  const url = `${NOMINATIM_BASE}/reverse?${params.toString()}`;

  return nominatimFetch(url, timeoutMs).then((data) => {
    const lat = parseFloat(data.lat);
    const lng = parseFloat(data.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
      lat,
      lng,
      display_name: data.display_name || `${lat}, ${lng}`,
    };
  });
}
